<script>
import theme from "#build/ui/carousel";
</script>

<script setup>
import { computed, ref, watch, onMounted } from "vue";
import useEmblaCarousel from "embla-carousel-vue";
import { Primitive, useForwardProps } from "reka-ui";
import { reactivePick, computedAsync } from "@vueuse/core";
import { useAppConfig } from "#imports";
import { useLocale } from "../composables/useLocale";
import { tv } from "../utils/tv";
import UButton from "./Button.vue";
const props = defineProps({
  as: { type: null, required: false },
  prev: { type: Object, required: false },
  prevIcon: { type: String, required: false },
  next: { type: Object, required: false },
  nextIcon: { type: String, required: false },
  arrows: { type: Boolean, required: false, default: false },
  dots: { type: Boolean, required: false, default: false },
  orientation: { type: null, required: false, default: "horizontal" },
  items: { type: Array, required: false },
  autoplay: { type: [Boolean, Object], required: false, default: false },
  autoScroll: { type: [Boolean, Object], required: false, default: false },
  autoHeight: { type: [Boolean, Object], required: false, default: false },
  classNames: { type: [Boolean, Object], required: false, default: false },
  fade: { type: [Boolean, Object], required: false, default: false },
  wheelGestures: { type: Boolean, required: false, skipCheck: true, default: false },
  class: { type: null, required: false },
  ui: { type: null, required: false },
  align: { type: [String, Function], required: false, default: "center" },
  containScroll: { type: [Boolean, String], required: false, default: "trimSnaps" },
  slidesToScroll: { type: [String, Number], required: false, default: 1 },
  dragFree: { type: Boolean, required: false, default: false },
  dragThreshold: { type: Number, required: false, default: 10 },
  inViewThreshold: { type: null, required: false, default: 0 },
  loop: { type: Boolean, required: false, default: false },
  skipSnaps: { type: Boolean, required: false, default: false },
  duration: { type: Number, required: false, default: 25 },
  startIndex: { type: Number, required: false, default: 0 },
  watchDrag: { type: [Boolean, Function], required: false, default: true },
  watchResize: { type: [Boolean, Function], required: false, default: true },
  watchSlides: { type: [Boolean, Function], required: false, default: true },
  watchFocus: { type: [Boolean, Function], required: false, default: true },
  active: { type: Boolean, required: false, default: true },
  breakpoints: { type: Object, required: false, default: () => ({}) }
});
defineSlots();
const emits = defineEmits(["select"]);
const { dir, t } = useLocale();
const appConfig = useAppConfig();
const rootProps = useForwardProps(reactivePick(props, "active", "align", "breakpoints", "containScroll", "dragFree", "dragThreshold", "duration", "inViewThreshold", "loop", "skipSnaps", "slidesToScroll", "startIndex", "watchDrag", "watchResize", "watchSlides", "watchFocus"));
const prevIcon = computed(() => props.prevIcon || (dir.value === "rtl" ? appConfig.ui.icons.arrowRight : appConfig.ui.icons.arrowLeft));
const nextIcon = computed(() => props.nextIcon || (dir.value === "rtl" ? appConfig.ui.icons.arrowLeft : appConfig.ui.icons.arrowRight));
const ui = computed(() => tv({ extend: tv(theme), ...appConfig.ui?.carousel || {} })({
  orientation: props.orientation
}));
const options = computed(() => ({
  ...props.fade ? { align: "center", containScroll: false } : {},
  ...rootProps.value,
  axis: props.orientation === "horizontal" ? "x" : "y",
  direction: dir.value === "rtl" ? "rtl" : "ltr"
}));
const plugins = computedAsync(async () => {
  const plugins2 = [];
  if (props.autoplay) {
    const AutoplayPlugin = await import("embla-carousel-autoplay").then((r) => r.default);
    plugins2.push(AutoplayPlugin(typeof props.autoplay === "boolean" ? {} : props.autoplay));
  }
  if (props.autoScroll) {
    const AutoScrollPlugin = await import("embla-carousel-auto-scroll").then((r) => r.default);
    plugins2.push(AutoScrollPlugin(typeof props.autoScroll === "boolean" ? {} : props.autoScroll));
  }
  if (props.autoHeight) {
    const AutoHeightPlugin = await import("embla-carousel-auto-height").then((r) => r.default);
    plugins2.push(AutoHeightPlugin(typeof props.autoHeight === "boolean" ? {} : props.autoHeight));
  }
  if (props.classNames) {
    const ClassNamesPlugin = await import("embla-carousel-class-names").then((r) => r.default);
    plugins2.push(ClassNamesPlugin(typeof props.classNames === "boolean" ? {} : props.classNames));
  }
  if (props.fade) {
    const FadePlugin = await import("embla-carousel-fade").then((r) => r.default);
    plugins2.push(FadePlugin(typeof props.fade === "boolean" ? {} : props.fade));
  }
  if (props.wheelGestures) {
    const { WheelGesturesPlugin } = await import("embla-carousel-wheel-gestures");
    plugins2.push(WheelGesturesPlugin(typeof props.wheelGestures === "boolean" ? {} : props.wheelGestures));
  }
  return plugins2;
});
const [emblaRef, emblaApi] = useEmblaCarousel(options.value, plugins.value);
watch([options, plugins], () => {
  emblaApi.value?.reInit(options.value, plugins.value);
});
function scrollPrev() {
  emblaApi.value?.scrollPrev();
}
function scrollNext() {
  emblaApi.value?.scrollNext();
}
function scrollTo(index) {
  emblaApi.value?.scrollTo(index);
}
function onKeyDown(event) {
  const prevKey = props.orientation === "vertical" ? "ArrowUp" : "ArrowLeft";
  const nextKey = props.orientation === "vertical" ? "ArrowDown" : "ArrowRight";
  if (event.key === prevKey) {
    event.preventDefault();
    scrollPrev();
    return;
  }
  if (event.key === nextKey) {
    event.preventDefault();
    scrollNext();
  }
}
const canScrollNext = ref(false);
const canScrollPrev = ref(false);
const selectedIndex = ref(0);
const scrollSnaps = ref([]);
function onInit(api) {
  scrollSnaps.value = api?.scrollSnapList() || [];
}
function onSelect(api) {
  canScrollNext.value = api?.canScrollNext() || false;
  canScrollPrev.value = api?.canScrollPrev() || false;
  selectedIndex.value = api?.selectedScrollSnap() || 0;
  emits("select", selectedIndex.value);
}
onMounted(() => {
  if (!emblaApi.value) {
    return;
  }
  emblaApi.value?.on("init", onInit);
  emblaApi.value?.on("init", onSelect);
  emblaApi.value?.on("reInit", onInit);
  emblaApi.value?.on("reInit", onSelect);
  emblaApi.value?.on("select", onSelect);
});
defineExpose({
  emblaRef,
  emblaApi
});
</script>

<template>
  <Primitive
    :as="as"
    role="region"
    aria-roledescription="carousel"
    tabindex="0"
    :class="ui.root({ class: [props.class, props.ui?.root] })"
    @keydown="onKeyDown"
  >
    <div ref="emblaRef" :class="ui.viewport({ class: props.ui?.viewport })">
      <div :class="ui.container({ class: props.ui?.container })">
        <div
          v-for="(item, index) in items"
          :key="index"
          role="group"
          aria-roledescription="slide"
          :class="ui.item({ class: props.ui?.item })"
        >
          <slot :item="item" :index="index" />
        </div>
      </div>
    </div>

    <div v-if="arrows || dots" :class="ui.controls({ class: props.ui?.controls })">
      <div v-if="arrows" :class="ui.arrows({ class: props.ui?.arrows })">
        <UButton
          :disabled="!canScrollPrev"
          :icon="prevIcon"
          size="md"
          color="neutral"
          variant="outline"
          :aria-label="t('carousel.prev')"
          v-bind="typeof prev === 'object' ? prev : void 0"
          :class="ui.prev({ class: props.ui?.prev })"
          @click="scrollPrev"
        />
        <UButton
          :disabled="!canScrollNext"
          :icon="nextIcon"
          size="md"
          color="neutral"
          variant="outline"
          :aria-label="t('carousel.next')"
          v-bind="typeof next === 'object' ? next : void 0"
          :class="ui.next({ class: props.ui?.next })"
          @click="scrollNext"
        />
      </div>

      <div v-if="dots" :class="ui.dots({ class: props.ui?.dots })">
        <template v-for="(_, index) in scrollSnaps" :key="index">
          <button
            :aria-label="t('carousel.goto', { slide: index + 1 })"
            :class="ui.dot({ class: props.ui?.dot, active: selectedIndex === index })"
            @click="scrollTo(index)"
          />
        </template>
      </div>
    </div>
  </Primitive>
</template>
